import type { DBSchema, IDBPDatabase } from 'idb';
import { openStore, openWithin, reportOpenFailure, sweepTemporaryStores, temporaryStoreName } from './storeOpen.js';
import type { FieldKeyString, FieldValue, FieldVersion, Hlc, Uuid } from '@colplat/protocol';
import { compareHlc, parseFieldKey } from '@colplat/protocol';

/**
 * IndexedDB as cache-only, never system of record. This is what makes Safari
 * evicting IndexedDB after ~7 days of no interaction a non-issue by design:
 * an empty cache isn't data loss, it just triggers `fetchRecord` again.
 * Everything here is cheap to lose. The only genuinely unrecoverable
 * client-side data is the unsent outbox, which lives in `queue.ts`, not here.
 *
 * Reads are synchronous (`getField`/`getRecord`/`getHistory`) against an
 * in-memory mirror — IndexedDB is written through on every change, but a UI
 * cannot await a render.
 */

export interface FieldSnapshot {
  key: FieldKeyString;
  entity: string;
  recordId: Uuid;
  field: string;
  value: FieldValue;
  version: number;
  clock: Hlc;
  updatedAt: string;
}

const HISTORY_CAP = 20;

interface ReplicaSchema extends DBSchema {
  fields: {
    key: FieldKeyString;
    value: FieldSnapshot;
    indexes: { by_record: [string, string] };
  };
  history: {
    key: string;
    value: FieldVersion;
    indexes: { by_key: FieldKeyString };
  };
  meta: {
    key: string;
    value: string;
  };
}

export type FetchRecord = (
  entity: string,
  recordId: Uuid,
) => Promise<{ fields: FieldSnapshot[]; history?: FieldVersion[] }>;

export interface ReplicaOptions {
  appId: string;
  env: 'prod' | 'test';
  /** The manifest's `schemaVersion`. A cache filled under a different one is dropped — see `open`. */
  schemaVersion: number;
  fetchRecord: FetchRecord;
}

export class Replica {
  private db: IDBPDatabase<ReplicaSchema> | undefined;
  private readonly fields = new Map<FieldKeyString, FieldSnapshot>();
  private readonly history = new Map<FieldKeyString, FieldVersion[]>();
  private readonly loadedRecords = new Set<string>();
  private readonly listeners = new Set<() => void>();

  constructor(private readonly opts: ReplicaOptions) {}

  /**
   * Opened through `openStore`, so it lets go when another tab needs it, and
   * — being a cache like the row store — a temporary one stands in when it
   * will not open within `fallbackAfterMs` (see `Dataset.open`, 2026-10-21).
   */
  async open(opts: { fallbackAfterMs?: number } = {}): Promise<void> {
    const dbName = `colplat-${this.opts.appId}-${this.opts.env}`;
    const upgrade = (db: IDBPDatabase<ReplicaSchema>): void => {
      const fields = db.createObjectStore('fields', { keyPath: 'key' });
      fields.createIndex('by_record', ['entity', 'recordId']);
      const history = db.createObjectStore('history', { keyPath: 'id' });
      history.createIndex('by_key', 'key');
      db.createObjectStore('meta');
    };
    const real = openStore<ReplicaSchema>(dbName, 1, upgrade);
    let opened: IDBPDatabase<ReplicaSchema> | undefined;
    try {
      opened = opts.fallbackAfterMs === undefined ? await real : await openWithin(real, opts.fallbackAfterMs, dbName);
    } catch (err) {
      if (opts.fallbackAfterMs === undefined) throw err;
      reportOpenFailure(dbName, err);
    }
    if (opened) {
      this.db = opened;
      sweepTemporaryStores(dbName);
    } else {
      void real.then((late) => late.close()).catch(() => undefined);
      this.temporaryName = temporaryStoreName(dbName);
      this.db = await openStore<ReplicaSchema>(this.temporaryName, 1, upgrade);
    }
    await this.dropCacheFilledUnderAnotherSchema();
  }

  private temporaryName: string | undefined;

  /** Remove the temporary store, if this page made one. */
  discardTemporary(): void {
    if (!this.temporaryName) return;
    this.db?.close();
    indexedDB.deleteDatabase(this.temporaryName);
  }

  /**
   * A record cached before a field existed would never show that field.
   *
   * `ensureRecordLoaded` is a cold-cache check: anything in `fields` for that
   * record counts as loaded, and no later reload refetches it. That is right
   * for values, which arrive as `committed` messages — and wrong for the set
   * of fields itself, which only changes when the manifest does. Adding
   * `wallet.shortName` and seeding it with a migration left every device that
   * had opened a wallet showing an empty Short box, permanently, with the
   * value sitting in the database.
   *
   * `schemaVersion` is already the thing that says "what you know about this
   * app's shape is out of date" — see the manifest's own notes on it. So the
   * cache carries the version it was filled under, and a mismatch empties it.
   * Cheap, by this file's first principle: nothing here is a system of
   * record, and an empty cache is a `fetchRecord` away from full.
   *
   * A store written before this existed has no stamp, so it is dropped once.
   */
  private async dropCacheFilledUnderAnotherSchema(): Promise<void> {
    const db = this.requireDb();
    const stamp = String(this.opts.schemaVersion);
    const seen = await db.get('meta', 'schemaVersion');
    if (seen === stamp) return;
    const tx = db.transaction(['fields', 'history', 'meta'], 'readwrite');
    await tx.objectStore('fields').clear();
    await tx.objectStore('history').clear();
    await tx.objectStore('meta').put(stamp, 'schemaVersion');
    await tx.done;
    this.fields.clear();
    this.history.clear();
    this.loadedRecords.clear();
  }

  private requireDb(): IDBPDatabase<ReplicaSchema> {
    if (!this.db) throw new Error('Replica.open() must be awaited before use');
    return this.db;
  }

  getField(key: FieldKeyString): FieldSnapshot | undefined {
    return this.fields.get(key);
  }

  getRecord(entity: string, recordId: Uuid): FieldSnapshot[] {
    return [...this.fields.values()].filter((f) => f.entity === entity && f.recordId === recordId);
  }

  /** Newest first, capped. */
  getHistory(key: FieldKeyString): FieldVersion[] {
    return this.history.get(key) ?? [];
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  /**
   * Bring a record's fields into memory, and make sure they are current.
   *
   * The cache was authoritative once loaded, which is wrong for a reason
   * that took a prod incident to see: a value can be written where no
   * `committed` message is sent — a migration, an importer, a bank sync, any
   * REST path that does not broadcast. Those never reached a device that had
   * the record, no matter how often it reloaded.
   *
   * What that looked like: `wallet.shortName` was added, the deploy created
   * the column, and the backfill filled it twenty minutes later. A device
   * that opened the wallet in between cached the record WITHOUT that field —
   * correctly, it did not exist yet — and then showed an empty box forever.
   * Typing into it wrote against base 0 against a server at version 1, so
   * the value would not save either, and the field went to `review`.
   *
   * So the cache paints, and the server confirms. The fetch is awaited
   * because every caller opens a record before showing a form, which is
   * exactly the moment to be right rather than fast — and it is the same
   * request a cold cache has always made here.
   */
  async ensureRecordLoaded(
    entity: string, recordId: Uuid, hold: (key: FieldKeyString) => boolean = () => false,
  ): Promise<void> {
    const recordKey = `${entity}:${recordId}`;
    const db = this.requireDb();

    if (this.loadedRecords.has(recordKey)) {
      await this.refreshRecord(entity, recordId, hold);
      return;
    }

    const cached = await db.getAllFromIndex('fields', 'by_record', [entity, recordId]);

    if (cached.length > 0) {
      for (const snap of cached) this.fields.set(snap.key, snap);
      for (const snap of this.getRecord(entity, recordId)) {
        await this.loadHistoryIntoMemory(db, snap.key);
      }
      this.loadedRecords.add(recordKey);
      this.notify();
      await this.refreshRecord(entity, recordId, hold);
      return;
    }
    {
      const fetched = await this.opts.fetchRecord(entity, recordId);
      const tx = db.transaction(['fields', 'history', 'meta'], 'readwrite');
      for (const snap of fetched.fields) {
        this.fields.set(snap.key, snap);
        await tx.objectStore('fields').put(snap);
      }
      for (const version of fetched.history ?? []) {
        await tx.objectStore('history').put(version);
      }
      await tx.objectStore('meta').put(new Date().toISOString(), 'lastRehydratedAt');
      await tx.done;
    }

    for (const snap of this.getRecord(entity, recordId)) {
      await this.loadHistoryIntoMemory(db, snap.key);
    }
    this.loadedRecords.add(recordKey);
    this.notify();
  }

  /**
   * Ask the server what this record's fields say now, and take what is newer.
   *
   * Three things it will not do, each one a way of making a cache refresh
   * worse than no refresh at all:
   *
   *  - **Empty means "could not ask".** `fetchRecord` answers `{ fields: [] }`
   *    for an offline device and for a request that failed, and every record
   *    has at least a `created` field, so nothing is the one answer that
   *    cannot be true. Treating it as truth would empty the cache precisely
   *    when the cache is all there is.
   *  - **Never backwards.** A snapshot at or below the version held is
   *    ignored, so a reply that crossed a `committed` message on the wire
   *    cannot undo it.
   *  - **Never over an edit in progress.** `hold` is the caller's answer to
   *    "am I editing this?" — invariant #5. A field the user holds keeps the
   *    draft in the box; the server's value is already queued for release by
   *    whoever owns that decision.
   */
  async refreshRecord(
    entity: string, recordId: Uuid, hold: (key: FieldKeyString) => boolean = () => false,
  ): Promise<void> {
    const fetched = await this.opts.fetchRecord(entity, recordId);
    if (fetched.fields.length === 0) return;

    const db = this.requireDb();
    const fresh = fetched.fields.filter((snap) => {
      if (hold(snap.key)) return false;
      const have = this.fields.get(snap.key);
      return have === undefined || snap.version > have.version;
    });
    if (fresh.length === 0) return;

    const tx = db.transaction(['fields', 'history'], 'readwrite');
    for (const snap of fresh) {
      this.fields.set(snap.key, snap);
      await tx.objectStore('fields').put(snap);
    }
    for (const version of fetched.history ?? []) {
      await tx.objectStore('history').put(version);
    }
    await tx.done;
    for (const snap of fresh) await this.loadHistoryIntoMemory(db, snap.key);
    this.notify();
  }

  async applyCommitted(version: FieldVersion): Promise<void> {
    const db = this.requireDb();
    const { entity, recordId, field } = parseFieldKey(version.key);
    const snap: FieldSnapshot = {
      key: version.key, entity, recordId, field,
      value: version.value, version: version.version, clock: version.clock, updatedAt: version.wallTime,
    };
    this.fields.set(version.key, snap);

    const tx = db.transaction(['fields', 'history'], 'readwrite');
    await tx.objectStore('fields').put(snap);
    await tx.objectStore('history').put(version);
    await tx.done;

    await this.capHistory(db, version.key);
    await this.loadHistoryIntoMemory(db, version.key);
    this.notify();
  }

  /**
   * Records a remote commit in history WITHOUT touching the live value —
   * hard invariant 5: never repaint a value under an active cursor. Used
   * while the local user holds the field; the value catches up via
   * `applyCommitted` only once they release.
   */
  async recordHistoryOnly(version: FieldVersion): Promise<void> {
    const db = this.requireDb();
    await db.put('history', version);
    await this.capHistory(db, version.key);
    await this.loadHistoryIntoMemory(db, version.key);
    this.notify();
  }

  private async loadHistoryIntoMemory(db: IDBPDatabase<ReplicaSchema>, key: FieldKeyString): Promise<void> {
    const rows = await db.getAllFromIndex('history', 'by_key', key);
    rows.sort((a, b) => compareHlc(b.clock, a.clock)); // newest first
    this.history.set(key, rows.slice(0, HISTORY_CAP));
  }

  private async capHistory(db: IDBPDatabase<ReplicaSchema>, key: FieldKeyString): Promise<void> {
    const rows = await db.getAllFromIndex('history', 'by_key', key);
    if (rows.length <= HISTORY_CAP) return;
    rows.sort((a, b) => compareHlc(a.clock, b.clock)); // oldest first
    const excess = rows.slice(0, rows.length - HISTORY_CAP);
    const tx = db.transaction('history', 'readwrite');
    for (const row of excess) await tx.store.delete(row.id);
    await tx.done;
  }
}
